import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice, media } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { StatusPill, type PillTone } from '../../../components/panel';
import { formatDate } from '../../../lib/format';
import { formatCredits } from '../../../lib/credits';

const MUTED = 'var(--rr-color-text-muted)';
const TONE: Record<voice.VoiceApplication['status'], PillTone> = { submitted: 'submitted', approved: 'approved', rejected: 'rejected', withdrawn: 'neutral' };

/**
 * Getting a number from us, for an account that has none of its own.
 *
 * The page says what it costs before anything is asked: a registration fee
 * once, and a rental each month, both from credits that were bought. Applying
 * holds the fee; nothing is taken until the application is approved, and a
 * withdrawal or a rejection gives all of it back.
 */
export function Applications({ status }: { status: voice.VoiceStatus }) {
  const t = useT();
  const qc = useQueryClient();
  const quote = useQuery({ queryKey: ['voice-application-quote'], queryFn: () => voice.getApplicationQuote(), retry: false });
  const list = useQuery({ queryKey: ['voice-applications'], queryFn: () => voice.listApplications(), retry: false, refetchInterval: 20_000 });
  const [open, setOpen] = useState(false);
  const q = quote.data?.data;
  const rows = list.data?.data ?? [];
  const waiting = rows.some((a) => a.status === 'submitted');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['voice-applications'] });
    void qc.invalidateQueries({ queryKey: ['voice-numbers'] });
    void qc.invalidateQueries({ queryKey: ['voice-status'] });
  };

  const withdraw = useMutation({
    mutationFn: (a: voice.VoiceApplication) => voice.withdrawApplication(a.id),
    onSuccess: () => { refresh(); notifySuccess(t('voice.apply.withdrawn')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  // Nothing to offer and nothing to show: the card is not there at all.
  if (!q || (!q.open && rows.length === 0)) return null;
  const canApply = q.open && q.priced && !waiting;

  return (
    <Card withBorder data-testid="voice-apply">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
            <Title order={4}>{t('voice.apply.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('voice.apply.intro')}</Text>
          </Stack>
          {q.open && <Button size="xs" disabled={!canApply} onClick={() => setOpen(true)}>{t('voice.apply.go')}</Button>}
        </Group>
        {q.open && q.priced && (
          <dl className="rr-voice__facts" data-testid="voice-apply-costs">
            <dt>{t('voice.apply.cost.setup')}</dt><dd>{formatCredits(q.setup_minor, t)}</dd>
            <dt>{t('voice.apply.cost.monthly')}</dt><dd>{formatCredits(q.monthly_minor, t)}</dd>
          </dl>
        )}
        {q.open && q.priced && <Text role="caption" c={MUTED}>{t('voice.apply.cost.note')} <Link to="/billing/topup">{t('voice.apply.cost.topup')}</Link></Text>}
        {q.open && !q.priced && <Text role="bodySmall" c={MUTED}>{t('voice.apply.notYet')}</Text>}
        {rows.map((a) => (
          <div key={a.id} className="rr-voice__row" data-testid="voice-application">
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Group gap="xs" wrap="wrap">
                <Text role="bodySmall" fw={650}>{a.number ?? t(`voice.apply.kind.${a.number_kind}`)}</Text>
                <StatusPill tone={TONE[a.status]}>{t(`voice.apply.status.${a.status}`)}</StatusPill>
              </Group>
              <Text role="caption" c={MUTED}>{t(`voice.apply.line.${a.status}`, { when: formatDate(a.created_at), fee: formatCredits(a.fee_minor, t) })}</Text>
              {a.status === 'rejected' && a.reject_reason && <Text role="caption">{a.reject_reason}</Text>}
            </Stack>
            {a.status === 'submitted' && (
              <Button
                size="xs" variant="subtle" color="error" loading={withdraw.isPending && withdraw.variables?.id === a.id}
                onClick={() => openConfirmModal({
                  title: t('voice.apply.withdrawTitle'),
                  children: <Text role="bodySmall">{t('voice.apply.withdrawBody', { fee: formatCredits(a.fee_minor, t) })}</Text>,
                  labels: { confirm: t('voice.apply.withdraw'), cancel: t('action.cancel') },
                  onConfirm: () => withdraw.mutate(a)
                })}
              >{t('voice.apply.withdraw')}</Button>
            )}
          </div>
        ))}
      </Stack>
      <ApplyForm opened={open} quote={q} termsVersion={status.terms_version ?? undefined} onClose={() => setOpen(false)} onDone={refresh} />
    </Card>
  );
}

function ApplyForm({ opened, quote, termsVersion, onClose, onDone }: { opened: boolean; quote: voice.VoiceApplicationQuote; termsVersion?: string; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [kind, setKind] = useState<'national' | 'geographic'>('national');
  const [business, setBusiness] = useState('');
  const [registration, setRegistration] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [purpose, setPurpose] = useState('');
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { if (opened) setAgreed(false); }, [opened]);
  // RICA: one upload for each document the platform asks for (its own list, 2 October 2026). Each file
  // goes to the organisation's media library first; the application carries its id.
  const required = quote.required_documents ?? [];
  const [files, setFiles] = useState<Record<string, { id: string; name: string }>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  async function attach(requirement: string, file: File | null) {
    if (!file) return;
    setUploading(requirement);
    try {
      const up = await media.upload(file, { filename: file.name });
      setFiles((f) => ({ ...f, [requirement]: { id: up.data.id, name: file.name } }));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('voice.apply.form.uploadFailed'));
    } finally {
      setUploading(null);
    }
  }

  const send = useMutation({
    mutationFn: () => voice.apply({
      number_kind: kind, terms_version: termsVersion,
      details: { business_name: business.trim(), registration_number: registration.trim(), contact_name: contact.trim(), contact_phone: phone.trim(), address: address.trim(), purpose: purpose.trim() },
      documents: required.filter((r) => files[r]).map((r) => ({ requirement: r, media_id: files[r].id }))
    }),
    onSuccess: () => { onDone(); onClose(); notifySuccess(t('voice.apply.sent')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });
  const ready = business.trim() && contact.trim() && phone.trim() && purpose.trim() && agreed && required.every((r) => files[r]) && !uploading;

  return (
    <Drawer opened={opened} onClose={onClose} title={t('voice.apply.form.title')} position="right" size="lg">
      <Stack gap="md" data-testid="voice-apply-form">
        <Alert color="info">{t('voice.apply.form.held', { fee: formatCredits(quote.setup_minor, t), monthly: formatCredits(quote.monthly_minor, t) })}</Alert>
        <Select
          label={t('voice.apply.form.kind')} description={t(`voice.apply.form.kindHint.${kind}`)}
          value={kind} onChange={(e) => setKind(e.currentTarget.value as 'national' | 'geographic')}
          data={[{ value: 'national', label: t('voice.apply.kind.national') }, { value: 'geographic', label: t('voice.apply.kind.geographic') }]}
        />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput label={t('voice.apply.form.business')} description={t('voice.apply.form.businessHint')} value={business} maxLength={160} onChange={(e) => setBusiness(e.currentTarget.value)} />
          <TextInput label={t('voice.apply.form.registration')} description={t('voice.apply.form.registrationHint')} value={registration} maxLength={40} onChange={(e) => setRegistration(e.currentTarget.value)} />
          <TextInput label={t('voice.apply.form.contact')} description={t('voice.apply.form.contactHint')} value={contact} maxLength={120} onChange={(e) => setContact(e.currentTarget.value)} />
          <TextInput label={t('voice.apply.form.phone')} description={t('voice.apply.form.phoneHint')} value={phone} placeholder="082 555 0100" onChange={(e) => setPhone(e.currentTarget.value)} />
        </SimpleGrid>
        <TextInput label={t('voice.apply.form.address')} description={t('voice.apply.form.addressHint')} value={address} maxLength={300} onChange={(e) => setAddress(e.currentTarget.value)} />
        <Textarea label={t('voice.apply.form.purpose')} description={t('voice.apply.form.purposeHint')} value={purpose} maxLength={500} minRows={2} onChange={(e) => setPurpose(e.currentTarget.value)} />
        {required.length > 0 && (
          <Stack gap="xs" data-testid="voice-apply-documents">
            <Text role="label">{t('voice.apply.form.documents')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('voice.apply.form.documentsHint')}</Text>
            {required.map((r) => (
              <Group key={r} justify="space-between" wrap="nowrap" gap="sm">
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text role="bodySmall">{r}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{files[r] ? t('voice.apply.form.attached', { name: files[r].name }) : t('voice.apply.form.notAttached')}</Text>
                </Stack>
                <FilePick
                  accept="application/pdf,image/jpeg,image/png"
                  onPick={(f) => void attach(r, f)}
                  loading={uploading === r}
                  label={files[r] ? t('voice.apply.form.replace') : t('voice.apply.form.attach')}
                  ariaLabel={t('voice.apply.form.attachFor', { name: r })}
                />
              </Group>
            ))}
          </Stack>
        )}
        <Checkbox checked={agreed} onChange={(v) => setAgreed(v)} label={t('voice.apply.form.agree', { fee: formatCredits(quote.setup_minor, t) })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={send.isPending} disabled={!ready} onClick={() => send.mutate()}>{t('voice.apply.form.send')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

/** A button that opens the file chooser: the form keeps its own button style, the browser keeps its picker. */
function FilePick({ accept, onPick, loading, label, ariaLabel }: { accept: string; onPick: (file: File | null) => void; loading: boolean; label: string; ariaLabel: string }) {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        aria-label={ariaLabel}
        onChange={(e) => { onPick(e.currentTarget.files?.[0] ?? null); e.currentTarget.value = ''; }}
      />
      <Button size="xs" variant="default" loading={loading} onClick={() => input.current?.click()}>{label}</Button>
    </>
  );
}
